import CompanyEditForm from "./CompanyEditForm";
import React, { useEffect, useState } from "react";
import { API_URL } from "../../config";

const SuperadminDashboard = ({ onLogout }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    const loadCompanies = async () => {
      try {
        const token = localStorage.getItem("token");

        const response = await fetch(`${API_URL}/api/companies`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        const data = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(
            data?.error || "Unternehmen konnten nicht geladen werden."
          );
        }

        if (!Array.isArray(data)) {
          throw new Error("Ungültige Antwort der Unternehmensverwaltung.");
        }

        setCompanies(data);
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadCompanies();

    return () => controller.abort();
  }, []);
  const emptyForm = {
    name: "",
    email: "",
    phone: "",
    address: "",
    adminEmail: "",
    adminPassword: "",
  };

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState("");
const [editingCompany, setEditingCompany] = useState(null);
const [editSuccess, setEditSuccess] = useState("");
  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (saving) return;

    setFormError("");
    setSuccess("");

    if (new TextEncoder().encode(form.adminPassword).length > 72) {
      setFormError("Das Passwort darf höchstens 72 UTF-8-Bytes enthalten.");
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      setFormError("Bitte erneut anmelden.");
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/companies`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.error || "Unternehmen konnte nicht erstellt werden."
        );
      }

      // Nach bestätigtem Speichern sofort das Passwort entfernen.
      setForm(emptyForm);
      setSuccess("Unternehmen und Administrator wurden angelegt.");

      // Die Liste separat aktualisieren: Ein Ladefehler bedeutet
      // nicht, dass das bereits bestätigte Anlegen fehlgeschlagen ist.
      try {
        const listResponse = await fetch(`${API_URL}/api/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const list = await listResponse.json().catch(() => null);

        if (!listResponse.ok || !Array.isArray(list)) {
          throw new Error("Liste konnte nicht aktualisiert werden.");
        }

        setCompanies(list);
        setError("");
      } catch {
        setSuccess(
          "Unternehmen und Administrator wurden angelegt. " +
          "Bitte lade die Seite neu, um die aktualisierte Liste zu sehen."
        );
      }
    } catch (err) {
      setFormError(
        err instanceof TypeError
          ? "Keine Serverantwort erhalten. Prüfe vor einem erneuten Versuch, ob das Unternehmen bereits angelegt wurde."
          : err.message
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="admin-brand">
          <h1>QRoof – Superadmin</h1>
        </div>

        <button type="button" onClick={onLogout}>
          Abmelden
        </button>
      </header>

      <main className="admin-content">
        <h2>Unternehmensverwaltung</h2>
        {editSuccess && <p role="status">{editSuccess}</p>}

{editingCompany && (
  <CompanyEditForm
    key={editingCompany.id}
    company={editingCompany}
    onCancel={() => setEditingCompany(null)}
    onSaved={updated => {
      setCompanies(previous =>
        previous.map(company =>
          company.id === updated.id ? updated : company
        )
      );
      setEditingCompany(null);
      setEditSuccess("Unternehmen gespeichert.");
    }}
  />
)}
        <section className="admin-panel">
          <h3>Unternehmen anlegen</h3>

          <form onSubmit={handleCreate}>
            <fieldset disabled={saving}>
              <legend>Unternehmensdaten</legend>

              <p>
                <label htmlFor="company-name">Unternehmensname *</label>
                <br />
                <input
                  id="company-name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  maxLength={255}
                />
              </p>

              <p>
                <label htmlFor="company-email">Unternehmens-E-Mail</label>
                <br />
                <input
                  id="company-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  maxLength={255}
                />
              </p>

              <p>
                <label htmlFor="company-phone">Telefon</label>
                <br />
                <input
                  id="company-phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  maxLength={50}
                />
              </p>

              <p>
                <label htmlFor="company-address">Adresse</label>
                <br />
                <textarea
                  id="company-address"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  maxLength={500}
                  rows={3}
                />
              </p>
            </fieldset>

            <fieldset disabled={saving}>
              <legend>Erster Unternehmensadministrator</legend>

              <p>
                <label htmlFor="company-admin-email">
                  Administrator-E-Mail *
                </label>
                <br />
                <input
                  id="company-admin-email"
                  name="adminEmail"
                  type="email"
                  value={form.adminEmail}
                  onChange={handleChange}
                  autoComplete="off"
                  required
                  maxLength={255}
                />
              </p>

              <p>
                <label htmlFor="company-admin-password">
                  Administratorpasswort *
                </label>
                <br />
                <input
                  id="company-admin-password"
                  name="adminPassword"
                  type="password"
                  value={form.adminPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                  required
                  minLength={10}
                  maxLength={72}
                  aria-describedby="company-password-hint"
                />
                <br />
                <small id="company-password-hint">
                  Mindestens 10 Zeichen, höchstens 72 UTF-8-Bytes.
                  Umlaute benötigen mehr als ein Byte.
                </small>
              </p>
            </fieldset>

            {formError && <p role="alert">{formError}</p>}
            {success && <p role="status">{success}</p>}

            <button type="submit" disabled={saving}>
              {saving ? "Wird angelegt …" : "Unternehmen anlegen"}
            </button>
          </form>
        </section>
        {loading && <p>Unternehmen werden geladen ...</p>}

        {error && <p role="alert">{error}</p>}

        {!loading && !error && (
          <div className="admin-panel">
            <p>{companies.length} Unternehmen</p>

            {companies.length === 0 ? (
              <p>Noch keine Unternehmen vorhanden.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Unternehmen</th>
                    <th>E-Mail</th>
                    <th>Status</th>
                    <th>Archivierung</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((company) => (
                    <tr key={company.id}>
                      <td>{company.name}</td>
                      <td>{company.email || "–"}</td>
                      <td>
                        {Number(company.is_active) === 1
                          ? "Aktiv"
                          : "Inaktiv"}
                      </td>
                      <td>
                        {Number(company.auto_archive_enabled) === 1
                          ? `${company.archive_after_months} Monate`
                          : "Deaktiviert"}
                      </td>
                      <td>
  <button
    type="button"
    disabled={Boolean(editingCompany)}
    onClick={() => {
      setEditSuccess("");
      setEditingCompany(company);
    }}
  >
    Bearbeiten
  </button>
</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default SuperadminDashboard;